import React, { useContext, useEffect, useState } from 'react'
import { api } from '../../utils'
import { Link, useHistory } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import { useWindowSize } from '../../hooks'
import { Button, Card, CardHeader, Row, Table } from 'reactstrap'
import { ToastContainer } from 'react-toastify'
import { volumeReportFilterDataContext } from '../../HOC/volumeReportFiltersContext'
import SetTitle from '../../utils/set_title'
import { notify } from '../../utils/notify'
import { getDownloadFilename, triggerBlobDownload } from '../../utils/download'
import VolumeRetrieveHistoricalRecovery from './VolumeRetrieveHistoricalRecovery'

const getLocaleParts = (localeValue) =>
  String(localeValue || '')
    .split(',')
    .map((locale) => locale.trim())
    .filter(Boolean)

function VolumeRetrieveListing() {
  SetTitle('Market Screener')

  const [volumeReportScheduled, setVolumeReportScheduled] = useState([])
  const [pages, setPages] = useState(0)
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const history = useHistory()
  const windowSize = useWindowSize()
  const itemPerPage = 10

  const [filteredData, , getFilteredData, queryParams, actualPage, setActualPage] = useContext(
    volumeReportFilterDataContext
  )

  async function getData(page = actualPage) {
    api
      .get('volume-report-scheduled/', {
        params: {
          page_size: itemPerPage,
          page,
        },
      })
      .then((response) => {
        setVolumeReportScheduled(response.data.results)
        setPages(Math.ceil(response.data.count / itemPerPage))
      })
  }

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo report?')) {
      return
    }

    try {
      await api.post(`volume-report-scheduled/${id}/soft-delete/`)
      setVolumeReportScheduled((previousItems) => previousItems.filter((item) => item.id !== id))
      notify('Report eliminato con successo.', 'success')
    } catch (error) {
      notify(error?.response?.data?.error || 'Impossibile eliminare il report.', 'error')
    }
  }

  const handleDownloadKeywordsFile = async (id) => {
    try {
      const response = await api.get(`/market-screener-detail/${id}/download-keywords`, {
        responseType: 'blob',
      })
      const fileName = getDownloadFilename(
        response.headers?.['content-disposition'],
        'market_screener_keywords.xlsx'
      )
      triggerBlobDownload(response.data, fileName)
    } catch (downloadError) {
      notify('Impossibile scaricare le keyword.', 'error')
    }
  }

  const handlePageClick = (data) => {
    const currentPage = data.selected + 1
    setActualPage(currentPage)

    if (Object.keys(filteredData).length > 0) {
      getFilteredData({
        ...queryParams,
        page_size: itemPerPage,
        page: currentPage,
      })
      return
    }

    getData(currentPage)
  }

  useEffect(() => {
    if (filteredData.results) {
      setVolumeReportScheduled(filteredData.results)
      setPages(Math.ceil(filteredData.count / itemPerPage))
      return
    }

    setPages(0)
    getData()
  }, [filteredData])

  const icon = <i className="fa-solid fa-plus"></i>
  const scheduleTableMinWidth = '1340px'
  const scheduleHeaderStyle = {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'space-between',
    padding: '1.5rem',
  }
  const centeredHeaderStyle = { fontSize: '12px', textAlign: 'center', width: '75px' }
  const ellipsisCellStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }
  const localeCellStyle = {
    textAlign: 'left',
    width: '130px',
    maxWidth: '130px',
    overflow: 'hidden',
    whiteSpace: 'normal',
  }
  const localeListStyle = {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px 6px',
    justifyContent: 'flex-start',
    lineHeight: '1.35',
  }
  const localeBadgeStyle = {
    display: 'inline-block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
  }
  const actionButtonStyle = {
    fontSize: windowSize.width <= 1280 ? '12px' : '13px',
    whiteSpace: 'nowrap',
  }

  const buildBigQueryTableUrl = (tableReference) => {
    const [project, datasetName, bigqueryTableName] = (tableReference || '').split('.')

    if (!project || !datasetName || !bigqueryTableName) {
      return null
    }

    return (
      `https://console.cloud.google.com/bigquery?project=${encodeURIComponent(project)}` +
      `&p=${encodeURIComponent(project)}` +
      `&d=${encodeURIComponent(datasetName)}` +
      `&t=${encodeURIComponent(bigqueryTableName)}` +
      `&page=table`
    )
  }

  return (
    <>
      <VolumeRetrieveHistoricalRecovery
        isOpen={!!selectedSchedule}
        onClose={() => setSelectedSchedule(null)}
        scheduleId={selectedSchedule?.id}
        initialDetails={selectedSchedule}
      />
      <Row style={{ marginBottom: '100px' }}>
        <div className="col pb-5">
          <Card className="shadow">
            <CardHeader className="border-0" style={scheduleHeaderStyle}>
              <h1 className="mb-0">Market Screener</h1>
              <Button
                color="info"
                title="Create a new Market Screener"
                onClick={() => history.push('create-market-screener')}
                icon={icon}
              >
                + Add new
              </Button>
            </CardHeader>
            <Table
              className="align-items-center table-flush"
              responsive
              style={{ minWidth: scheduleTableMinWidth, tableLayout: 'fixed', width: '100%' }}
            >
              <thead className="thead-light">
                <tr>
                  <th scope="col" title="Report name" style={{ fontSize: '12px', width: '165px' }}>
                    Name
                  </th>
                  <th
                    scope="col"
                    title="Cliente"
                    style={{ fontSize: '12px', width: '135px', whiteSpace: 'nowrap' }}
                  >
                    Cliente
                  </th>
                  <th scope="col" title="Number of executions" style={centeredHeaderStyle}>
                    Runs
                  </th>
                  <th scope="col" title="Planned executions left" style={{ ...centeredHeaderStyle, width: '85px' }}>
                    Planned
                  </th>
                  <th
                    scope="col"
                    title="Last execution date"
                    style={{ fontSize: '12px', width: '100px', whiteSpace: 'nowrap' }}
                  >
                    Last Run
                  </th>
                  <th
                    scope="col"
                    title="Locales"
                    style={{ fontSize: '12px', textAlign: 'left', width: '130px', whiteSpace: 'nowrap' }}
                  >
                    Locales
                  </th>
                  <th scope="col" title="Keywords" style={{ ...centeredHeaderStyle, width: '65px' }}>
                    KW
                  </th>
                  <th
                    scope="col"
                    title="BigQuery table"
                    style={{ fontSize: '12px', width: '180px', whiteSpace: 'nowrap' }}
                  >
                    BQ Table
                  </th>
                  <th scope="col" title="Execution list" style={{ ...centeredHeaderStyle, width: '90px' }}>
                    Reports
                  </th>
                  <th
                    scope="col"
                    style={{ fontSize: '12px', textAlign: 'center', width: '320px' }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {volumeReportScheduled.map((item) => {
                  const bigQueryTableUrl = buildBigQueryTableUrl(item.bq_table_name)
                  const localeParts = getLocaleParts(item.locale)

                  return (
                    <tr key={item.id}>
                      <td style={ellipsisCellStyle} title={item.name || '-'}>
                        {item.name}
                      </td>
                      <td style={ellipsisCellStyle} title={item.cliente?.nome || '-'}>
                        {item.cliente?.nome || '-'}
                      </td>
                      <td style={{ textAlign: 'center' }} title={`Executions: ${item.number_of_runs || 0}`}>
                        {item.number_of_runs}
                      </td>
                      <td
                        style={{ textAlign: 'center' }}
                        title={`Planned executions left: ${item.remaining_planned_reports || 0}`}
                      >
                        {item.remaining_planned_reports}
                      </td>
                      <td style={ellipsisCellStyle} title={item.last_run || '-'}>
                        {item.last_run}
                      </td>
                      <td style={localeCellStyle} title={localeParts.join(', ') || '-'}>
                        {localeParts.length > 0 ? (
                          <span style={localeListStyle}>
                            {localeParts.map((locale, index) => (
                              <span key={`${locale}-${index}`} style={localeBadgeStyle}>
                                {locale}
                              </span>
                            ))}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }} title={`Keywords: ${item.keyword_count || 0}`}>
                        {item.keyword_count || 0}
                      </td>
                      <td style={ellipsisCellStyle} title={item.bq_table_name}>
                        {bigQueryTableUrl ? (
                          <a
                            href={bigQueryTableUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={item.bq_table_name}
                            style={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.bq_table_name}
                          </a>
                        ) : (
                          item.bq_table_name
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          size="sm"
                          aria-label="Open executions"
                          title="Open executions"
                          style={actionButtonStyle}
                          onClick={() => history.push(`/market-screener/executions/${item.id}`)}
                        >
                          Executions
                        </Button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div
                          className="d-inline-flex align-items-center justify-content-center"
                          style={{ gap: '8px', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}
                        >
                          <Button
                            color="info"
                            size="sm"
                            aria-label="Backfill historical data"
                            title="Backfill historical data"
                            style={actionButtonStyle}
                            onClick={() => setSelectedSchedule(item)}
                          >
                            Backfill
                          </Button>
                          {(item.has_keywords || Number(item.keyword_count) > 0) && (
                            <Button
                              color="secondary"
                              size="sm"
                              aria-label="Download keywords Excel"
                              title="Download keywords Excel"
                              style={actionButtonStyle}
                              onClick={() => handleDownloadKeywordsFile(item.id)}
                            >
                              Download KW
                            </Button>
                          )}
                          <Link to={`/market-screener/edit/${item.id}`}>
                            <Button color="primary" size="sm" title="Edit report" style={actionButtonStyle}>
                              Edit
                            </Button>
                          </Link>
                          <Button
                            color="danger"
                            size="sm"
                            title="Delete report"
                            style={actionButtonStyle}
                            onClick={() => handleSoftDelete(item.id)}
                          >
                            Elimina
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            {pages > 0 ? (
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                pageCount={pages}
                forcePage={actualPage - 1}
                onPageChange={handlePageClick}
                containerClassName={'pagination justify-content-center pt-5 pb-3'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            ) : null}
            <ToastContainer />
          </Card>
        </div>
      </Row>
    </>
  )
}

export default VolumeRetrieveListing
